import { constant, flow } from 'fp-ts-esm/function';
import * as O from 'fp-ts-esm/Option';
import { pipe } from 'fp-ts-esm/pipeable';
import * as _ from './internal.js';
// -------------------------------------------------------------------------------------
// constructors
// -------------------------------------------------------------------------------------
/**
 * @category constructors
 * @since 2.3.8
 */
export const optional = _.optional;
/**
 * @category constructors
 * @since 2.3.0
 */
export const id = () => optional(O.some, constant);
// -------------------------------------------------------------------------------------
// converters
// -------------------------------------------------------------------------------------
/**
 * View a `Optional` as a `Traversal`.
 *
 * @category converters
 * @since 2.3.0
 */
export const asTraversal = _.optionalAsTraversal;
// -------------------------------------------------------------------------------------
// compositions
// -------------------------------------------------------------------------------------
/**
 * Compose a `Optional` with a `Optional`.
 *
 * @category compositions
 * @since 2.3.0
 */
export const compose = _.optionalComposeOptional;
/**
 * Alias of `compose`.
 *
 * @category compositions
 * @since 2.3.8
 */
export const composeOptional = compose;
/**
 * Compose a `Optional` with a `Iso`.
 *
 * @category compositions
 * @since 2.3.8
 */
export const composeIso = 
/*#__PURE__*/
flow(_.isoAsOptional, compose);
/**
 * Compose a `Optional` with a `Lens`.
 *
 * @category compositions
 * @since 2.3.7
 */
export const composeLens = 
/*#__PURE__*/
flow(_.lensAsOptional, _.optionalComposeOptional);
/**
 * Compose a `Optional` with a `Prism`.
 *
 * @category compositions
 * @since 2.3.7
 */
export const composePrism = 
/*#__PURE__*/
flow(_.prismAsOptional, _.optionalComposeOptional);
/**
 * Compose a `Optional` with an `Traversal`.
 *
 * @category compositions
 * @since 2.3.8
 */
export const composeTraversal = (ab) => flow(asTraversal, _.traversalComposeTraversal(ab));
// -------------------------------------------------------------------------------------
// combinators
// -------------------------------------------------------------------------------------
/**
 * @category combinators
 * @since 2.3.0
 */
export const modifyOption = _.optionalModifyOption;
/**
 * @category combinators
 * @since 2.3.7
 */
export const setOption = (a) => modifyOption(() => a);
/**
 * @category combinators
 * @since 2.3.0
 */
export const modify = _.optionalModify;
export function modifyF(F) {
    return (f) => (sa) => (s) => pipe(sa.getOption(s), O.fold(() => F.of(s), 
    // @ts-expect-error
    (a) => F.map(f(a), (a) => sa.set(a)(s))));
}
/**
 * Return an `Optional` from a `Optional` focused on a nullable value.
 *
 * @category combinators
 * @since 2.3.3
 */
export const fromNullable = 
/*#__PURE__*/
compose(/*#__PURE__*/ _.prismAsOptional(/*#__PURE__*/ _.prismFromNullable()));
export function filter(predicate) {
    return compose(_.prismAsOptional(_.prismFromPredicate(predicate)));
}
/**
 * Return a `Optional` from a `Optional` and a prop.
 *
 * @category combinators
 * @since 2.3.0
 */
export const prop = (prop) => compose(pipe(_.lensId(), _.lensProp(prop), _.lensAsOptional));
/**
 * Return a `Optional` from a `Optional` and a list of props.
 *
 * @category combinators
 * @since 2.3.0
 */
export const props = (...props) => compose(pipe(_.lensId(), _.lensProps(...props), _.lensAsOptional));
/**
 * Return a `Optional` from a `Optional` focused on a component of a tuple.
 *
 * @category combinators
 * @since 2.3.0
 */
export const component = (prop) => compose(pipe(_.lensId(), _.lensComponent(prop), _.lensAsOptional));
/**
 * Return a `Optional` from a `Optional` focused on an index of a `ReadonlyArray`.
 *
 * @category combinators
 * @since 2.3.0
 */
export const index = _.optionalIndex;
/**
 * Return a `Optional` from a `Optional` focused on an index of a `ReadonlyNonEmptyArray`.
 *
 * @category combinators
 * @since 2.3.8
 */
export const indexNonEmpty = _.optionalIndexNonEmpty;
/**
 * Return a `Optional` from a `Optional` focused on a key of a `ReadonlyRecord`.
 *
 * @category combinators
 * @since 2.3.0
 */
export const key = _.optionalKey;
/**
 * Return a `Optional` from a `Optional` focused on a required key of a `ReadonlyRecord`.
 *
 * @category combinators
 * @since 2.3.0
 */
export const atKey = (key) => (sa) => pipe(sa, compose(_.lensAsOptional(_.atReadonlyRecord().at(key))));
/**
 * Return a `Optional` from a `Optional` focused on the `Some` of a `Option` type.
 *
 * @category combinators
 * @since 2.3.0
 */
export const some = 
/*#__PURE__*/
compose(/*#__PURE__*/ _.prismAsOptional(/*#__PURE__*/ _.prismSome()));
/**
 * Return a `Optional` from a `Optional` focused on the `Right` of a `Either` type.
 *
 * @category combinators
 * @since 2.3.0
 */
export const right = 
/*#__PURE__*/
compose(/*#__PURE__*/ _.prismAsOptional(/*#__PURE__*/ _.prismRight()));
/**
 * Return a `Optional` from a `Optional` focused on the `Left` of a `Either` type.
 *
 * @category combinators
 * @since 2.3.0
 */
export const left = 
/*#__PURE__*/
compose(/*#__PURE__*/ _.prismAsOptional(/*#__PURE__*/ _.prismLeft()));
/**
 * Return a `Traversal` from a `Optional` focused on a `Traversable`.
 *
 * @category combinators
 * @since 2.3.0
 */
export function traverse(T) {
    return flow(asTraversal, _.traversalTraverse(T));
}
export function findFirst(predicate) {
    return compose(_.optionalFindFirst(predicate));
}
export function findFirstNonEmpty(predicate) {
    return compose(_.optionalFindFirstNonEmpty(predicate));
}
// -------------------------------------------------------------------------------------
// pipeables
// -------------------------------------------------------------------------------------
/**
 * @category Invariant
 * @since 2.3.0
 */
export const imap = (f, g) => (ea) => imap_(ea, f, g);
// -------------------------------------------------------------------------------------
// instances
// -------------------------------------------------------------------------------------
const imap_ = (ea, ab, ba) => optional(flow(ea.getOption, O.map(ab)), flow(ba, ea.set));
/**
 * @category instances
 * @since 2.3.0
 */
export const URI = 'monocle-ts/Optional';
/**
 * @category instances
 * @since 2.3.0
 */
export const Invariant = {
    URI,
    imap: imap_
};
/**
 * @category instances
 * @since 2.3.8
 */
export const Semigroupoid = {
    URI,
    compose: (ab, ea) => compose(ab)(ea)
};
/**
 * @category instances
 * @since 2.3.0
 */
export const Category = {
    URI,
    compose: Semigroupoid.compose,
    id
};
